<script setup lang="ts">
  import type { PropType } from 'vue';
  import type { Breadcrumb, Section } from '$types';

  type Module = {
    title: string | null,
    description_short: string | null,
    maps_link: string | null,
  }

  defineProps({
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
    module: { type: Object as PropType<Module>, required: true },
    sections: { type: Array as PropType<Section[]>, required: true },
    departments: { type: Array, required: true },
    main: { type: Object, required: true },
  });
</script>
<template>
  <main>
    <SfBreadcrumbs :breadcrumbs="breadcrumbs" class="container" />
    <SectionRenderer :sections="sections" placement="before" />
    <div class="contact-wrapper container">
      <section>
        <header>
          <SegmentHeading larger type="h1">
            {{ module.title }}
          </SegmentHeading>
          <div v-if="module.description_short" v-html="module.description_short" />
        </header>
        <h2 class="contact__form__header">
          {{ $t('Masz pytania? Napisz do nas.') }}
        </h2>
        <div class="contact__form__container">
          <SfContactForm :departments="departments" />
        </div>
      </section>
      <section class="contact">
        <div class="contact__details">
          <iframe v-if="module.maps_link" :src="module.maps_link" style="border:0;" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade" class="contact__details-map"></iframe>

          <div class="contact__main">
            <a :href="main.maps || '#'" :title="$t('Zobacz na mapach')" class="contact__main__address" rel="noreferrer nofollow noopener" target="_blank">
              <IconGeoAlt class="contact__main__address__icon" />
              <span class="contact__main__address__lines">
                <span v-if="main.name" v-text="main.name" />
                <span v-if="main.first_line" v-text="main.first_line" />
                <span v-if="main.second_line" v-text="main.second_line" />
                <span v-if="main.third_line" v-text="main.third_line" />
              </span>
            </a>
            <a v-if="main.phone" :href="`tel:${main.phone}`" class="contact__main__phone">
              <IconPhone class="contact__main__phone__icon" />
              <span v-html="main.phone" />
            </a>
            <a v-if="main.email" :href="`mailto:${main.email}`" class="contact__main__email">
              <IconEnvelope class="contact__main__email__icon" />
              <span v-html="main.email" />
            </a>
          </div>
        </div>
      </section>
    </div>
    <SectionRenderer :sections="sections" placement="after" />
  </main>
</template>
<style lang="scss" scoped>

  main {
    max-width: 1920px;
    margin: 0 auto;
  }

  .contact-wrapper {
    display: flex;
    flex-direction: column;
  }

  .contact {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    margin: 100px 0;

    &__details {
      display: grid;
      grid-template-columns: 3fr 2fr;
      align-items: center;
      justify-items: center;
      width: 100%;
    }

    &__details-map {
      width: 100%;
      height: 450px;
      max-height: 50svh;
    }

    &__main {
      display: flex;
      flex-direction: column;
      gap: 2.5rem;

      a {
        transition-property: color;
        transition-duration: var(--transition-primary-duration);
        transition-timing-function: var(--transition-primary-timing-function);

        &:focus {
          color: var(--primary-color);
        }

        @media (hover: hover) {
          &:hover {
            color: var(--primary-color);
          }
        }
      }

      &__address, &__phone, &__email {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: 1rem;
        line-height: 1.375rem;
        gap: 2rem;
        font-weight: var(--font-weight-600);

        &__icon {
          font-size: 2.5rem;
          line-height: 2.5rem;
        }
      }

      &__address {
        &__lines {
          display: flex;
          flex-direction: column;
        }
      }
    }

    &__form {
      display: flex;
      flex-direction: column;

      &__header {
        padding: 4rem 0 2rem;
        font-size: 1.5rem;
        font-weight: var(--font-weight-700);
      }
    }
  }

  @media (max-width: 1200px) {
    .contact__details {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }

    .contact {
      flex-direction: column;
      padding: 0;
      margin: 0;
    }
  }

  @media (max-width: 992px) {
    .contact-wrapper {
      padding: 0 1rem;
    }

    .contact {
      &__form {
        width: 100%;

        &__header {
          padding: 1rem 0
        }

        &__container {
          padding: 1rem 0 2.5rem;
        }
      }

      &__details {
        width: 100%;
        padding-bottom: 2.5rem;
      }

      &__main {
        gap: 1.5rem;

        &__address, &__phone, &__email {
          align-items: flex-start;
          gap: 1.5rem;

          &__icon {
            font-size: 1.5rem;
            line-height: 1.5rem;
          }
        }
      }
    }
  }
</style>
